import { 
  ArrowLeft, 
  CreditCard, 
  Clock, 
  FileText, 
  ShieldCheck, 
  Hash, 
  Building2, 
  User, 
  Layers,
  CheckCircle2,
  AlertCircle,
  Undo2,
  CalendarDays,
  Activity,
  Code
} from 'lucide-react';
import {
  DescriptionText,
  HorizontalStack,
  PaneSection,
  PropertyGrid,
  PropertyItem,
  SectionSeparator,
  Surface,
  VerticalStack,
  DisplayWorkspace,
  Badge,
  RegistryStatus,
  cn,
} from '@workspace/silk-ui';
import {
  type CommissionLedgerEntry,
  type CommissionLedgerLine,
} from '@/lib/commerce';

function formatDate(value?: string | null): string {
  if (!value) {
    return 'Not recorded';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString();
}

function formatJSON(value: unknown): string {
  return JSON.stringify(value ?? {}, null, 2);
}

function commissionOrderReference(entry: Pick<CommissionLedgerEntry, 'friendly_order_id' | 'order_id'>): string {
  return entry.friendly_order_id || entry.order_id;
}

function lineSummary(line: CommissionLedgerLine): string {
  return `${line.line_type} • ${line.amount} ${line.currency}`;
}

function commissionStatusVariant(status?: string | null): 'default' | 'success' | 'warning' | 'error' | 'muted' {
  switch ((status || '').toLowerCase()) {
    case 'paid':
      return 'success';
    case 'approved':
      return 'default';
    case 'reversed':
      return 'error';
    case 'earned':
    case 'pending':
      return 'warning';
    case 'void':
      return 'muted';
    default:
      return 'muted';
  }
}

interface CommissionLedgerEntryViewProps {
  entry: CommissionLedgerEntry | null;
  onClose: () => void;
  loading?: boolean;
}

export default function CommissionLedgerEntryView({ entry, onClose, loading }: CommissionLedgerEntryViewProps) {
  if (loading) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <VerticalStack gap={4} className="items-center text-center">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <DescriptionText className="text-sm font-bold animate-pulse">
            Loading entry details...
          </DescriptionText>
        </VerticalStack>
      </div>
    );
  }

  if (!entry) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <VerticalStack gap={4} className="items-center text-center max-w-sm">
          <div className="rounded-full bg-muted p-4">
            <AlertCircle className="h-8 w-8 text-muted-foreground" />
          </div>
          <div>
            <p className="text-lg font-black tracking-tight">Entry Not Found</p>
            <DescriptionText className="text-sm">
              The commission ledger entry you are looking for could not be loaded or no longer exists.
            </DescriptionText>
          </div>
          <button
            onClick={onClose}
            className="mt-2 rounded-md bg-primary px-6 py-2 text-sm font-black text-primary-foreground transition-all hover:opacity-90"
          >
            Back to Ledger
          </button>
        </VerticalStack>
      </div>
    );
  }

  const sidebar = (
    <VerticalStack gap={8}>
      <VerticalStack gap={4}>
        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/60 px-1">
          Quick Stats
        </p>
        <Surface className="rounded-xl border-border/40 bg-muted/5 p-4">
           <VerticalStack gap={4}>
              <VerticalStack gap={1}>
                <DescriptionText className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/60">
                  Total Value
                </DescriptionText>
                <p className="text-2xl font-black tracking-tighter text-foreground">
                  ${entry.amount} <span className="text-sm text-muted-foreground font-bold">{entry.currency}</span>
                </p>
              </VerticalStack>
              
              <VerticalStack gap={1}>
                <DescriptionText className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/60">
                  Status
                </DescriptionText>
                <div className="flex">
                  <RegistryStatus
                    label={entry.status}
                    variant={commissionStatusVariant(entry.status)}
                  />
                </div>
              </VerticalStack>

              <VerticalStack gap={1}>
                <DescriptionText className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/60">
                  Order Ref
                </DescriptionText>
                <p className="text-sm font-black text-foreground truncate">
                  {commissionOrderReference(entry)}
                </p>
              </VerticalStack>
           </VerticalStack>
        </Surface>
      </VerticalStack>

      <VerticalStack gap={4}>
        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/60 px-1">
          Metadata
        </p>
        <div className="space-y-4 px-1">
          <VerticalStack gap={1.5}>
            <div className="flex items-center gap-2 text-muted-foreground/60">
              <Hash className="h-3.5 w-3.5" />
              <span className="text-[10px] font-bold uppercase tracking-wide">Entry ID</span>
            </div>
            <p className="font-mono text-[11px] font-bold text-foreground bg-muted/20 p-1.5 rounded border border-border/20 break-all">
              {entry.commission_entry_id}
            </p>
          </VerticalStack>

          <VerticalStack gap={1.5}>
            <div className="flex items-center gap-2 text-muted-foreground/60">
              <Activity className="h-3.5 w-3.5" />
              <span className="text-[10px] font-bold uppercase tracking-wide">Last Sync</span>
            </div>
            <p className="text-xs font-bold text-foreground">
              {formatDate(entry.updated_at)}
            </p>
          </VerticalStack>
        </div>
      </VerticalStack>

      <div className="mt-auto pt-8">
        <div className="rounded-lg bg-primary/5 border border-primary/10 p-4">
          <div className="flex items-center gap-2 mb-2 text-primary">
            <ShieldCheck className="h-4 w-4" />
            <span className="text-xs font-black">Audit Verified</span>
          </div>
          <p className="text-[10px] text-muted-foreground leading-relaxed font-medium">
            This entry has been reconciled against the originating order and plan snapshots.
          </p>
        </div>
      </div>
    </VerticalStack>
  );

  return (
    <DisplayWorkspace
      isOpen={true}
      title="Commission Entry Detail"
      subtitle={`Viewing record for order ${commissionOrderReference(entry)}`}
      onClose={onClose}
      sidebar={sidebar}
      maxContentWidth="max-w-[1000px]"
    >
      <VerticalStack gap={10} className="pb-20">
        {/* Core Info */}
        <PaneSection 
          label="Identity & Source" 
          description="Originating entities and plan attribution for this commission record."
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Surface className="p-5 border-border/60 hover:border-primary/20 transition-colors">
              <HorizontalStack gap={4} className="items-start">
                <div className="rounded-lg bg-muted p-2.5">
                  <Building2 className="h-5 w-5 text-muted-foreground" />
                </div>
                <VerticalStack gap={1}>
                  <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/60">Selling Tenant</p>
                  <p className="text-sm font-black text-foreground">{entry.selling_tenant_id}</p>
                  <DescriptionText className="text-[10px]">The merchant or reseller that originated the sale.</DescriptionText>
                </VerticalStack>
              </HorizontalStack>
            </Surface>

            <Surface className="p-5 border-border/60 hover:border-primary/20 transition-colors">
              <HorizontalStack gap={4} className="items-start">
                <div className="rounded-lg bg-muted p-2.5">
                  <User className="h-5 w-5 text-muted-foreground" />
                </div>
                <VerticalStack gap={1}>
                  <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/60">Agent Attribution</p>
                  <p className="text-sm font-black text-foreground">{entry.agent_user_id || 'Unassigned'}</p>
                  <DescriptionText className="text-[10px]">The user credited for this transaction.</DescriptionText>
                </VerticalStack>
              </HorizontalStack>
            </Surface>

            <Surface className="p-5 border-border/60 hover:border-primary/20 transition-colors">
              <HorizontalStack gap={4} className="items-start">
                <div className="rounded-lg bg-muted p-2.5">
                  <Layers className="h-5 w-5 text-muted-foreground" />
                </div>
                <VerticalStack gap={1}>
                  <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/60">Commission Plan</p>
                  <p className="text-sm font-black text-foreground">{entry.commission_plan_id || 'Global Default'}</p>
                  <DescriptionText className="text-[10px]">The logic used to calculate this amount.</DescriptionText>
                </VerticalStack>
              </HorizontalStack>
            </Surface>

            <Surface className="p-5 border-border/60 hover:border-primary/20 transition-colors">
              <HorizontalStack gap={4} className="items-start">
                <div className="rounded-lg bg-muted p-2.5">
                  <CreditCard className="h-5 w-5 text-muted-foreground" />
                </div>
                <VerticalStack gap={1}>
                  <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/60">Transaction Ref</p>
                  <p className="text-sm font-black text-foreground truncate max-w-[200px]">{entry.order_id}</p>
                  <DescriptionText className="text-[10px]">System UUID for the source order.</DescriptionText>
                </VerticalStack>
              </HorizontalStack>
            </Surface>
          </div>
        </PaneSection>

        <SectionSeparator />

        {/* Timeline */}
        <PaneSection 
          label="Lifecycle Timeline"
          description="Key milestones for this entry from accrual to payout."
        >
          <div className="relative pl-8 space-y-8 before:absolute before:left-[11px] before:top-2 before:bottom-2 before:w-[2px] before:bg-muted/40">
             {/* Earned */}
             <div className="relative">
                <div className={cn(
                  "absolute -left-[27px] top-0 flex h-[24px] w-[24px] items-center justify-center rounded-full border-4 border-background ring-1 ring-border/40",
                  entry.earned_at ? "bg-amber-500" : "bg-muted"
                )}>
                  <Clock className="h-2.5 w-2.5 text-white" />
                </div>
                <HorizontalStack className="items-center justify-between">
                  <VerticalStack gap={0.5}>
                    <p className="text-sm font-black">Commission Earned</p>
                    <DescriptionText className="text-[11px]">Calculated and recorded in the ledger.</DescriptionText>
                  </VerticalStack>
                  <Badge variant="outline" className="font-mono text-[10px]">{formatDate(entry.earned_at)}</Badge>
                </HorizontalStack>
             </div>

             {/* Approved */}
             <div className="relative">
                <div className={cn(
                  "absolute -left-[27px] top-0 flex h-[24px] w-[24px] items-center justify-center rounded-full border-4 border-background ring-1 ring-border/40",
                  entry.approved_at ? "bg-blue-500" : "bg-muted"
                )}>
                  <CheckCircle2 className="h-2.5 w-2.5 text-white" />
                </div>
                <HorizontalStack className="items-center justify-between">
                  <VerticalStack gap={0.5}>
                    <p className="text-sm font-black">Admin Approved</p>
                    <DescriptionText className="text-[11px]">Verified for accuracy and eligibility.</DescriptionText>
                  </VerticalStack>
                  <Badge variant="outline" className="font-mono text-[10px]">{formatDate(entry.approved_at)}</Badge>
                </HorizontalStack>
             </div>

             {/* Paid */}
             <div className="relative">
                <div className={cn(
                  "absolute -left-[27px] top-0 flex h-[24px] w-[24px] items-center justify-center rounded-full border-4 border-background ring-1 ring-border/40",
                  entry.paid_at ? "bg-emerald-500" : "bg-muted"
                )}>
                  <CreditCard className="h-2.5 w-2.5 text-white" />
                </div>
                <HorizontalStack className="items-center justify-between">
                  <VerticalStack gap={0.5}>
                    <p className="text-sm font-black">Payout Executed</p>
                    <DescriptionText className="text-[11px]">Funds transferred to the selling partner.</DescriptionText>
                  </VerticalStack>
                  <Badge variant="outline" className="font-mono text-[10px]">{formatDate(entry.paid_at)}</Badge>
                </HorizontalStack>
             </div>

             {/* Reversed (Optional) */}
             {entry.reversed_at && (
                <div className="relative">
                  <div className="absolute -left-[27px] top-0 flex h-[24px] w-[24px] items-center justify-center rounded-full border-4 border-background ring-1 ring-border/40 bg-destructive">
                    <Undo2 className="h-2.5 w-2.5 text-white" />
                  </div>
                  <HorizontalStack className="items-center justify-between">
                    <VerticalStack gap={0.5}>
                      <p className="text-sm font-black text-destructive">Commission Reversed</p>
                      <DescriptionText className="text-[11px]">Transaction voided or credited back.</DescriptionText>
                    </VerticalStack>
                    <Badge variant="destructive" className="font-mono text-[10px]">{formatDate(entry.reversed_at)}</Badge>
                  </HorizontalStack>
                </div>
             )}
          </div>
        </PaneSection>

        <SectionSeparator />

        {/* Calculation Logic */}
        <PaneSection 
          label="Calculation Ledger Lines" 
          description="Detailed breakdown of individual line items that comprise the total commission amount."
        >
          <VerticalStack gap={3}>
            {(entry.lines || []).length === 0 ? (
              <Surface className="border-dashed border-border/60 p-8 text-center bg-muted/5">
                <DescriptionText className="text-xs italic">
                  No individual ledger lines were returned for this commission entry.
                </DescriptionText>
              </Surface>
            ) : (
              (entry.lines || []).map((line, idx) => (
                <Surface key={line.commission_line_id} className="p-0 overflow-hidden border-border/40 hover:border-primary/20 transition-all">
                  <div className="bg-muted/10 border-b border-border/40 px-4 py-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                       <span className="text-[10px] font-black text-muted-foreground/60">Line #{idx + 1}</span>
                       <Badge variant="secondary" className="text-[9px] uppercase tracking-wider font-black py-0 px-1.5 h-4">
                          {line.line_type}
                       </Badge>
                    </div>
                    <span className="text-[10px] font-mono text-muted-foreground/60">{line.commission_line_id}</span>
                  </div>
                  <div className="p-4">
                    <HorizontalStack className="items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                         <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                            <CreditCard className="h-4 w-4" />
                         </div>
                         <p className="text-lg font-black tracking-tight">${line.amount} <span className="text-xs text-muted-foreground font-bold">{line.currency}</span></p>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground/60">
                         <CalendarDays className="h-3.5 w-3.5" />
                         <span className="text-xs font-bold">{formatDate(line.created_at)}</span>
                      </div>
                    </HorizontalStack>
                    <PropertyGrid cols={2} className="bg-muted/5 rounded-lg p-3 border border-border/20">
                      <PropertyItem label="Order Line Link" value={line.order_line_id || 'Global/Header Level'} />
                      <PropertyItem label="Accrual Basis" value={line.line_type === 'flat' ? 'Fixed Amount' : 'Percentage Scale'} />
                    </PropertyGrid>
                  </div>
                </Surface>
              ))
            )}
          </VerticalStack>
        </PaneSection>

        <SectionSeparator />

        {/* Raw Data */}
        <PaneSection 
          label="Technical Snapshot" 
          description="The immutable state of calculation variables at the time of entry creation."
        >
          <Surface className="p-0 overflow-hidden border-border/40 bg-muted/20">
            <div className="border-b border-border/40 px-4 py-2 flex items-center justify-between bg-background">
               <div className="flex items-center gap-2 text-muted-foreground">
                  <Code className="h-4 w-4" />
                  <span className="text-xs font-black uppercase tracking-widest">snapshot.json</span>
               </div>
               <button 
                onClick={() => {
                  void navigator.clipboard.writeText(formatJSON(entry.calculation_snapshot));
                }}
                className="text-[10px] font-black text-primary hover:underline"
               >
                 Copy to clipboard
               </button>
            </div>
            <pre className="p-6 text-[11px] font-mono leading-relaxed text-foreground/80 overflow-x-auto max-h-[400px]">
              {formatJSON(entry.calculation_snapshot)}
            </pre>
          </Surface>
        </PaneSection>
      </VerticalStack>
    </DisplayWorkspace>
  );
}
